import { MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router";
import { useState, useEffect } from "react";

const messages = [
  {
    id: 1,
    text: "Christmas Special!\nEnjoy 10% off your next visit. See you soon!",
    bgColor: "bg-gradient-to-br from-orange-100 to-orange-50",
    textColor: "text-orange-900",
    accentColor: "text-orange-600",
    icon: "🎄"
  },
  {
    id: 2,
    text: "Festive Offer 🎅\nTake 15% off your next purchase. Don't miss it!",
    bgColor: "bg-gradient-to-br from-amber-100 to-yellow-50",
    textColor: "text-amber-900",
    accentColor: "text-amber-600",
    icon: "🎅"
  },
  {
    id: 3,
    text: "New Year Deal!\nStart 2025 with 10% off your next visit.",
    bgColor: "bg-gradient-to-br from-[#EAF7F5] to-[#E9F6F6]",
    textColor: "text-[#1C1C1C]",
    accentColor: "text-[#008B8B]",
    icon: "🎆"
  }
];

export const SMSCampaignCard = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % messages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <div className="group relative p-5 sm:p-6 rounded-[20px] bg-gradient-to-br from-white to-[#F9FBFB] border border-border/50 shadow-[0_4px_20px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_30px_rgba(0,140,131,0.12)] transition-all duration-300 cursor-pointer overflow-hidden">
      {/* Floating visual elements */}
      <div className="absolute top-6 right-12 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center animate-fade-in opacity-70 hidden lg:flex">
        <MessageCircle className="w-4 h-4 text-primary" />
      </div>
      <div
        className="absolute bottom-8 right-16 w-6 h-6 bg-primary/15 rounded-full flex items-center justify-center animate-fade-in opacity-60 hidden lg:flex"
        style={{ animationDelay: "0.2s" }}
      >
        <Send className="w-3 h-3 text-primary" />
      </div>

      <div className="relative flex flex-col md:flex-row gap-4 md:gap-4 items-center">
        {/* Left side - Phone mockup */}
        {/* Mobile: 68% width, centered | Tablet: 35% | Desktop: 40% */}
        <div
          className="w-[68%] max-w-[260px] md:max-w-none md:w-[35%] xl:w-[40%] flex-shrink-0 flex flex-col items-center mx-auto md:mx-0"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Phone wrapper with glow */}
          <div
            className="relative animate-fade-in w-full"
            style={{ maxHeight: "88%" }}
          >
            {/* Soft gradient glow around phone */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#008C83]/20 via-transparent to-[#008C83]/10 rounded-[32px] blur-2xl scale-110 group-hover:opacity-80 transition-opacity" />

            {/* Phone frame - responsive sizing with clamp */}
            <div
              className="relative mx-auto rounded-[28px] bg-gradient-to-br from-gray-900 via-gray-850 to-gray-900 p-[2.5px] shadow-[0_7px_28px_rgba(0,0,0,0.14)] transition-transform duration-300 group-hover:scale-[1.02]"
              style={{
                width: "clamp(160px, 100%, 240px)",
                aspectRatio: "9 / 19",
                objectFit: "contain"
              }}
            >
              {/* Phone notch - reduced by 30% */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-[8px] bg-black rounded-b-lg z-10" />

              {/* Screen content with subtle inner shadow */}
              <div className="h-full bg-gradient-to-br from-white to-gray-50 rounded-[26px] p-4 flex flex-col justify-center shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)] relative overflow-hidden">
                {/* Dynamic message bubbles */}
                {messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`absolute inset-4 flex items-center transition-all duration-1000 ${
                      index === currentIndex
                        ? "opacity-100 translate-y-0"
                        : index < currentIndex
                          ? "opacity-0 -translate-y-4"
                          : "opacity-0 translate-y-4"
                    }`}
                  >
                    <div
                      className={`${message.bgColor} rounded-[18px] rounded-bl-md p-3 sm:p-4 max-w-[92%] mx-auto shadow-sm`}
                    >
                      <div className="flex items-start gap-1.5 mb-1">
                        <span className="text-xs">{message.icon}</span>
                      </div>
                      <p
                        className={`text-[10px] sm:text-[11px] leading-relaxed ${message.textColor} font-normal whitespace-pre-line`}
                      >
                        {message.text.split(/(\d+%)/g).map((part, i) =>
                          /\d+%/.test(part) ? (
                            <span
                              key={i}
                              className={`font-bold ${message.accentColor}`}
                            >
                              {part}
                            </span>
                          ) : (
                            part
                          )
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination dots */}
          <div className="flex gap-1.5 mt-3">
            {messages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? "w-6 h-1.5 bg-[#008C83]"
                    : "w-1.5 h-1.5 bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to message ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Right side - Text + CTA */}
        {/* Mobile: 100% stacked | Tablet: 65% | Desktop: 60% */}
        <div className="w-full md:w-[65%] xl:w-[60%] flex flex-col justify-center text-center md:text-left space-y-3 md:space-y-4">
          <div className="space-y-2">
            <h3 className="text-[18px] md:text-[20px] font-bold text-foreground leading-tight">
              Reach your customers instantly
            </h3>

            <p
              className="text-[14px] md:text-[15px] text-muted-foreground leading-relaxed mx-auto md:mx-0"
              style={{ maxWidth: "34ch" }}
            >
              Send festive offers or daily promotions — all from one simple
              place.
            </p>
          </div>

          <div className="flex justify-center md:justify-start pt-1">
            <Button
              className="bg-[#009688] hover:bg-[#00796B] text-white shadow-[0_4px_12px_rgba(0,150,136,0.25)] hover:shadow-[0_6px_20px_rgba(0,150,136,0.4)] transition-all rounded-xl px-[18px] py-[10px] gap-2.5 text-[15px] font-semibold min-h-[44px]"
              onClick={() => navigate("/campaigns/broadcast")}
            >
              📢 Send New Campaign
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
